import { stockPlans } from "@/api/stockPlan/useStockPlanQuery";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { DetailContainer } from "@/features/standard/detail/Container";
import { DetailContent } from "@/features/standard/detail/Content";
import { DetailWrapper } from "@/features/standard/detail/Wrapper";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { StockPlanDetailForm } from "@/features/stockPlan/form/detailForm";
import { MantineReactCommonTable } from "@/features/ui/mantineTable";
import { setStockPlanStatus, stockPlanStatusColor } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { Badge, Flex, Text } from "@mantine/core";
import { StockPlansGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "mantine-react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
const Detail = Object.assign(
  {},
  {
    Container: DetailContainer,
    Wrapper: DetailWrapper,
    Content: DetailContent,
  },
);

interface StockPlanFormProps {
  purchaseOrderItemID?: number;
}
export const StockPlanForm = (params: StockPlanFormProps) => {
  const { purchaseOrderItemID } = params;
  const { t } = useTranslation();
  const { openModal } = useModal();

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  // 각 컬럼 sort
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  // 각 컬럼 filter
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  // 전체 검색 (단, serchFields에 필드를 직접 입력해줘야함.)
  const [globalFilter, setGlobalFilter] = useState("");

  // sort를 id 별 string으로 만들어줌
  const sortString = sorting.map(({ id, desc }) => (desc ? `-${id}` : id)).join(",");

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  // filter를 id 별 like를 사용하여 단어가 포함되어있는지 확인
  const filterQuery: { [key: string]: { $iLike: string } } = columnFilters.reduce<{
    [key: string]: { $iLike: string };
  }>((acc, filter) => {
    acc[filter.id] = { $iLike: `%${filter.value}%` as string };
    return acc;
  }, {});

  const { data } = useQuery(
    stockPlans.get({
      query: {
        purchaseOrderItemId: purchaseOrderItemID,
      },
      populate: ["transportation", "quantities", "partner", "location", "lot"],
      pageSize: pagination.pageSize,
      page: pagination.pageIndex,
      search: globalFilter,
      sort: sortString || undefined,
    }),
  );

  const rows = (data?.data.rows ?? []) as StockPlansGet200ResponseRowsInner[];

  const columns = useMemo<MRT_ColumnDef<StockPlansGet200ResponseRowsInner>[]>(
    () => [
      {
        accessorKey: "code",
        header: t("stockPlan.code"),
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <StockPlanDetailForm stockPlanId={row.original.id} />,
                  null,
                  t("common.detail", { item: t("stockPlan.stockPlan") }),
                )
              }
            >
              {row.original.code}
            </LinkAnchor>
          );
        },
      },
      {
        accessorKey: "status",
        header: t("stockPlan.status"),
        Cell: ({ row }) => {
          return (
            <Badge
              variant="filled"
              color={stockPlanStatusColor[row.original.status as keyof typeof stockPlanStatusColor]}
            >
              {t(setStockPlanStatus(row.original?.status ?? ""))}
            </Badge>
          );
        },
      },
      {
        accessorKey: "scheduledAt",
        header: t("stockPlan.scheduledAt"),
        size: 150,
        Cell: ({ row }) => timeUtil(row.original.scheduledAt ?? ""),
      },
      {
        accessorKey: "partner.name",
        header: t("partner.name"),
        size: 150,
        Cell: ({ row }) => (
          <LinkAnchor
            onClick={() =>
              openModal(
                <PartnerDetailForm partnerCode={row.original.partnerCode} />,
                null,
                t("common.detail", { item: t("partner.partner") }),
              )
            }
          >
            {row.original.partner?.name}
          </LinkAnchor>
        ),
      },
      {
        accessorKey: "lotData",
        header: t("lot.lot"),
        Cell: ({ row }) => {
          return (
            <Flex gap="xs" direction={"column"}>
              <Text>
                {t("lot.name")}: {row.original.lotData?.name ? row.original.lotData.name : "-"}
              </Text>
              <Text>
                {t("lot.expiration")}: {""}
                {row.original.lotData?.expiration !== null
                  ? timeUtil(row.original.lotData?.expiration ?? "")
                  : "-"}
              </Text>
            </Flex>
          );
        },
      },
      {
        accessorKey: "quantity",
        header: t("inventory.quantity"),
      },
      {
        accessorKey: "sourceLocationName",
        header: t("stockPlan.sourceLocation"),
        size: 150,
        Cell: ({ row }) => (
          <LinkAnchor
            onClick={() =>
              openModal(
                <LocationDetailForm locationCode={row.original.sourceLocationCode} />,
                null,
                t("common.detail", { item: t("location.location") }),
              )
            }
          >
            {row.original.sourceLocationName}
          </LinkAnchor>
        ),
      },
      {
        accessorKey: "targetLocationName",
        header: t("stockPlan.targetLocation"),
        size: 150,
        Cell: ({ row }) => (
          <LinkAnchor
            onClick={() =>
              openModal(
                <LocationDetailForm locationCode={row.original.targetLocationCode} />,
                null,
                t("common.detail", { item: t("location.location") }),
              )
            }
          >
            {row.original.targetLocationName}
          </LinkAnchor>
        ),
      },
    ],
    [t, openModal],
  );

  return (
    <MantineReactCommonTable
      data={rows as StockPlansGet200ResponseRowsInner[]}
      columns={columns}
      totalCount={data?.data.total}
      totalPages={data?.data.totalPages}
      pagination={pagination} // pagination
      setPagination={setPagination}
      sorting={sorting} // 정렬
      setSorting={setSorting}
      columnFilters={columnFilters} // 컬럼별 검색
      setColumnFilters={setColumnFilters}
      globalFilter={globalFilter} // 전체 검색
      setGlobalFilter={setGlobalFilter}
      rowSelection={rowSelection} // 선택
      setRowSelection={setRowSelection}
      selectedRowIds={selectedRowIds} // 선택된 id
      setSelectedRowIds={setSelectedRowIds}
      enableTopToolbar={true}
    />
  );
};
